<template>
  <div class="return-order-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>返货管理</span>
          <div>
            <el-button type="success" @click="handleExport" :loading="exportLoading">导出返货单</el-button>
            <el-button type="primary" @click="handleCreate">新增返货单</el-button>
          </div>
        </div>
      </template>

      <!-- 搜索表单（参考合同管理样式） -->
      <el-form :model="queryParams" class="search-form" label-width="80px">
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item label="客户">
              <el-input
                v-model="queryParams.keyword"
                placeholder="请输入客户姓名"
                clearable
                style="width: 100%"
                @keyup.enter="handleSearch"
              />
            </el-form-item>
          </el-col>
          <el-col :span="4">
            <el-form-item label="状态">
              <el-select v-model="queryParams.status" placeholder="全部" clearable style="width: 100%">
                <el-option label="全部" value="" />
                <el-option label="草稿" value="draft" />
                <el-option label="待审批" value="pending" />
                <el-option label="审批中" value="approving" />
                <el-option label="已通过" value="approved" />
                <el-option label="已驳回" value="rejected" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="4">
            <el-form-item label="返货类型">
              <el-select v-model="queryParams.returnType" placeholder="全部" clearable style="width: 100%">
                <el-option label="全部" :value="null" />
                <el-option label="退货" :value="1" />
                <el-option label="返修" :value="3" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="7">
            <el-form-item label="返货日期">
              <el-date-picker
                v-model="returnDateRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="3">
            <el-form-item label-width="0">
              <div class="action-buttons-inline">
                <el-button type="primary" @click="handleSearch">查询</el-button>
                <el-button @click="handleReset">重置</el-button>
              </div>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <!-- 返货单列表 -->
      <el-table v-loading="loading" :data="returnOrderList" border stripe style="width: 100%">
        <el-table-column prop="returnOrderNo" label="返货单号" width="150" show-overflow-tooltip />
        <el-table-column prop="returnType" label="返货类型" width="100" align="center">
          <template #default="{ row }">
            {{ getReturnTypeName(row.returnType) }}
          </template>
        </el-table-column>
        <el-table-column prop="customerName" label="客户姓名" width="150" show-overflow-tooltip />
        <el-table-column prop="originalOrderNo" label="原订单号" width="150" show-overflow-tooltip />
        <el-table-column prop="warehouseName" label="仓库" width="140" show-overflow-tooltip />
        <el-table-column prop="returnDate" label="返货日期" width="140">
          <template #default="{ row }">
            {{ row.returnDate || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="returnReason" label="返货原因" min-width="160" show-overflow-tooltip />
        <el-table-column prop="returnAmount" label="返货金额" width="130" align="right">
          <template #default="{ row }">
            <span style="color: #f56c6c; font-weight: bold">¥{{ formatMoney(row.returnAmount || 0) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="120" align="center">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)" size="small">
              {{ getStatusName(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="320" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleView(row)">详情</el-button>
            <el-button
              type="warning"
              size="small"
              @click="handleEdit(row)"
              v-if="isDraftStatus(row.status)"
            >
              编辑
            </el-button>
            <el-tooltip content="修正并重新发起审批" placement="top">
              <el-button
                type="success"
                size="small"
                @click="handleCorrect(row)"
                v-if="isRejectedStatus(row.status)"
              >
                修正
              </el-button>
            </el-tooltip>
            <el-button
              type="danger"
              size="small"
              @click="handleWithdraw(row)"
              v-if="isPendingStatus(row.status)"
            >
              撤销
            </el-button>
            <el-button
              type="info"
              size="small"
              @click="handleViewApprovalProgress(row)"
              v-if="!isDraftStatus(row.status) && row.returnType !== 3"
            >
              审批进度
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.page"
          v-model:page-size="queryParams.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>
  </div>

  <!-- 返货单详情弹窗 -->
  <el-dialog
    v-model="detailVisible"
    title="返货单详情"
    width="70%"
    destroy-on-close
  >
    <div v-loading="detailLoading" style="min-height: 240px">
      <BusinessDetail
        v-if="detailBusinessId"
        business-type="RETURN_ORDER"
        :business-id="detailBusinessId"
      />
      <el-empty v-else description="未找到详情" />

      <div v-if="approvalRecords.length" style="margin-top: 16px">
        <h4 style="margin-bottom: 8px">审批记录</h4>
        <ApprovalTimeline :records="approvalRecords" />
      </div>
    </div>
    <template #footer>
      <div class="dialog-footer">
        <!-- 待审批：撤销 -->
        <div v-if="detailReturnOrder && isPendingStatus(detailReturnOrder.status)" class="footer-center">
          <el-button type="danger" size="large" style="width: 200px" @click="handleWithdrawInDetail">
            撤销
          </el-button>
        </div>
        <!-- 草稿：编辑 + 提交审批 -->
        <div v-else-if="detailReturnOrder && isDraftStatus(detailReturnOrder.status)" class="footer-buttons">
          <el-button @click="handleEdit(detailReturnOrder)">编辑</el-button>
          <div style="position: relative; display: inline-block;">
            <div v-if="isPersonalUser" style="position: absolute; top: -20px; right: 0; font-size: 12px; color: #67c23a; white-space: nowrap;">
              个人用户点提交审批直接通过
            </div>
            <el-button class="submit-approval-btn" @click="handleSubmitInDetail">提交审批</el-button>
          </div>
        </div>
        <!-- 已驳回：修正 -->
        <div
          v-else-if="detailReturnOrder && isRejectedStatus(detailReturnOrder.status)"
          class="footer-center"
        >
          <el-tooltip content="修正并重新发起审批" placement="top">
            <el-button type="success" size="large" style="width: 200px" @click="handleCorrectInDetail">
              修正
            </el-button>
          </el-tooltip>
        </div>
        <!-- 其他状态：关闭 -->
        <div v-else class="footer-right">
          <el-button @click="detailVisible = false">关闭</el-button>
        </div>
      </div>
    </template>
  </el-dialog>

  <!-- 审批进度弹窗（只读，显示审批节点） -->
  <el-dialog
    v-model="approvalProgressVisible"
    title="审批进度"
    width="600px"
    destroy-on-close
  >
    <div v-loading="approvalProgressLoading" style="min-height: 200px">
      <ApprovalTimeline :records="approvalProgressRecords" />
    </div>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, onMounted, watch, onActivated, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getReturnOrderList, withdrawReturnOrder, submitReturnOrder, getReturnOrderDetail, exportReturnOrderList } from '../api/salesman'
import { getApprovalRecords } from '@/api/salesman'
import BusinessDetail from '@/components/BusinessDetail.vue'
import ApprovalTimeline from '@/components/ApprovalTimeline.vue'

// 获取企业ID（用于判断是否是个人用户）
const getCompanyId = () => {
  try {
    const userInfoStr = localStorage.getItem('userInfo')
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr)
      return userInfo.companyId || null
    }
  } catch (e) {
    console.warn('解析用户信息失败', e)
  }
  return null
}

// 判断是否是个人用户（没有企业ID）
const isPersonalUser = computed(() => {
  return !getCompanyId()
})

const router = useRouter()
const route = useRoute()
const loading = ref(false)
const returnOrderList = ref([])
const total = ref(0)
const exportLoading = ref(false)
// 上次导出时间，用于限流
const lastExportTime = ref(0)
// 导出限流间隔时间（毫秒），3秒内不允许重复导出
const EXPORT_THROTTLE_INTERVAL = 3000
const queryParams = reactive({
  status: '',
  returnType: null,
  keyword: '',
  startDate: '',
  endDate: '',
  page: 1,
  pageSize: 10
})

// 详情弹窗
const detailVisible = ref(false)
const detailLoading = ref(false)
const detailBusinessId = ref(null)
const detailReturnOrder = ref(null)
const approvalRecords = ref([])
// 审批进度弹窗（只读）
const approvalProgressVisible = ref(false)
const approvalProgressLoading = ref(false)
const approvalProgressRecords = ref([])

// 返货日期范围（用于日期选择器）
const returnDateRange = ref([])

// 判断是否为草稿状态（兼容大小写）
const isDraftStatus = (status) => {
  if (!status) return false
  return String(status).toLowerCase() === 'draft'
}

// 判断是否为待审批状态（兼容大小写）
const isPendingStatus = (status) => {
  if (!status) return false
  return String(status).toLowerCase() === 'pending'
}

// 判断是否为已驳回状态（兼容大小写）
const isRejectedStatus = (status) => {
  if (!status) return false
  const statusUpper = String(status).toUpperCase()
  return statusUpper === 'REJECTED' || statusUpper === 'REJECT'
}

const getStatusName = (status) => {
  const key = String(status || '').toLowerCase()
  const map = {
    draft: '草稿',
    pending: '待审批',
    approving: '审批中',
    approved: '已通过',
    rejected: '已驳回',
    cancelled: '已撤销'
  }
  return map[key] || status || '-'
}

const getStatusType = (status) => {
  const key = String(status || '').toLowerCase()
  const map = {
    draft: 'info',
    pending: 'warning',
    approving: 'warning',
    approved: 'success',
    rejected: 'danger',
    cancelled: 'info'
  }
  return map[key] || 'info'
}

const getReturnTypeName = (type) => {
  const val = Number(type)
  if (val === 1) return '退货'
  if (val === 2) return '换货'
  if (val === 3) return '返修'
  return '-'
}

const formatMoney = (amount) => {
  if (!amount) return '0.00'
  return Number(amount).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const loadReturnOrderList = async () => {
  loading.value = true
  try {
    const res = await getReturnOrderList(queryParams)
    if (res.code === '000000') {
      returnOrderList.value = res.data?.records || res.data?.list || []
      total.value = res.data?.total || 0
    }
  } catch (error) {
    console.error('获取返货单列表失败:', error)
    ElMessage.error('获取返货单列表失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  // 处理返货日期范围
  if (returnDateRange.value && returnDateRange.value.length === 2) {
    queryParams.startDate = returnDateRange.value[0]
    queryParams.endDate = returnDateRange.value[1]
  } else {
    queryParams.startDate = ''
    queryParams.endDate = ''
  }
  
  queryParams.page = 1
  loadReturnOrderList()
}

const handleReset = () => {
  queryParams.status = ''
  queryParams.returnType = null
  queryParams.keyword = ''
  queryParams.startDate = ''
  queryParams.endDate = ''
  queryParams.page = 1
  returnDateRange.value = []
  loadReturnOrderList()
}

const handleWithdraw = async (row) => {
  try {
    await ElMessageBox.confirm('确定要撤销该返货单吗？', '提示', {
      type: 'warning',
      confirmButtonText: '确定',
      cancelButtonText: '取消'
    })
    const res = await withdrawReturnOrder({ id: row.id })
    if (res.code === '000000') {
      ElMessage.success('撤销成功，已回到草稿')
      // 撤销后自动切换到草稿筛选
      queryParams.status = 'draft'
      queryParams.page = 1
      loadReturnOrderList()
    } else {
      ElMessage.error(res.msg || '撤销返货单失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('撤销返货单失败:', error)
      ElMessage.error('撤销返货单失败')
    }
  }
}

const handleCreate = () => {
  router.push({ name: 'SalesmanReturnOrderForm' })
}

const handleView = async (row) => {
  detailVisible.value = true
  detailLoading.value = true
  detailBusinessId.value = row.id
  detailReturnOrder.value = row
  approvalRecords.value = []

  try {
    // 先加载业务员端详情，获取最新状态用于按钮展示
    const detailRes = await getReturnOrderDetail(row.id)
    if (detailRes.code === '000000' && detailRes.data) {
      detailReturnOrder.value = detailRes.data
    } else {
      detailReturnOrder.value = row
    }
    // 加载审批记录
    const res = await getApprovalRecords('RETURN_ORDER', row.id)
    if (res.code === '000000' && Array.isArray(res.data)) {
      approvalRecords.value = res.data
    }
  } catch (error) {
    console.error('获取返货单详情失败:', error)
    ElMessage.error('获取返货单详情失败')
  } finally {
    detailLoading.value = false
  }
}

// 详情弹窗：提交审批（草稿）
const handleSubmitInDetail = async () => {
  if (!detailReturnOrder.value || !detailReturnOrder.value.id) return
  detailLoading.value = true
  try {
    const res = await submitReturnOrder({ id: detailReturnOrder.value.id })
    if (res.code === '000000') {
      ElMessage.success('提交成功')
      detailVisible.value = false
      // 个人用户提交审批后，自动切换到已通过状态
      queryParams.status = isPersonalUser.value ? 'approved' : 'pending'
      queryParams.page = 1
      loadReturnOrderList()
    } else {
      ElMessage.error(res.msg || '提交失败')
    }
  } catch (error) {
    console.error('提交返货单失败:', error)
    ElMessage.error('提交失败')
  } finally {
    detailLoading.value = false
  }
}

// 详情弹窗：撤销（待审批）
const handleWithdrawInDetail = async () => {
  if (!detailReturnOrder.value || !detailReturnOrder.value.id) return
  try {
    await ElMessageBox.confirm('确定要撤销该返货单吗？', '提示', {
      type: 'warning',
      confirmButtonText: '确定',
      cancelButtonText: '取消'
    })
    const res = await withdrawReturnOrder({ id: detailReturnOrder.value.id })
    if (res.code === '000000') {
      ElMessage.success('撤销成功，已回到草稿')
      detailVisible.value = false
      // 撤销后自动切换到草稿筛选
      queryParams.status = 'draft'
      queryParams.page = 1
      loadReturnOrderList()
    } else {
      ElMessage.error(res.msg || '撤销失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('撤销返货单失败:', error)
      ElMessage.error('撤销失败')
    }
  }
}

// 详情弹窗：修正（已驳回）
const handleCorrectInDetail = () => {
  if (!detailReturnOrder.value || !detailReturnOrder.value.id) return
  router.push({ name: 'SalesmanReturnOrderForm', query: { correctId: detailReturnOrder.value.id, mode: 'correct' } })
  detailVisible.value = false
}

const handleEdit = (row) => {
  if (!isDraftStatus(row.status)) {
    ElMessage.warning('仅草稿状态的返货单可以编辑')
    return
  }
  router.push({ name: 'SalesmanReturnOrderForm', query: { id: row.id, mode: 'edit' } })
}

// 修正功能
const handleCorrect = (row) => {
  if (!row || !row.id) {
    ElMessage.warning('数据异常，无法修正')
    return
  }
  if (!isRejectedStatus(row.status)) {
    ElMessage.warning('只有已驳回状态的返货单可以修正')
    return
  }
  router.push({ name: 'SalesmanReturnOrderForm', query: { correctId: row.id, mode: 'correct' } })
}

// 查看审批进度
const handleViewApprovalProgress = async (row) => {
  approvalProgressVisible.value = true
  approvalProgressLoading.value = true
  approvalProgressRecords.value = []

  try {
    const res = await getApprovalRecords('RETURN_ORDER', row.id)
    if (res.code === '000000') {
      approvalProgressRecords.value = res.data || []
    } else {
      ElMessage.error(res.msg || '获取审批进度失败')
    }
  } catch (error) {
    console.error('获取审批进度失败:', error)
    ElMessage.error('获取审批进度失败')
  } finally {
    approvalProgressLoading.value = false
  }
}

const handleSizeChange = (val) => {
  queryParams.pageSize = val
  queryParams.page = 1
  loadReturnOrderList()
}

const handlePageChange = (val) => {
  queryParams.page = val
  loadReturnOrderList()
}

// 导出返货单列表
const handleExport = async () => {
  // 限流检查：如果正在导出，直接返回
  if (exportLoading.value) {
    return
  }

  // 限流检查：如果距离上次导出时间太短，提示用户
  const now = Date.now()
  const timeSinceLastExport = now - lastExportTime.value
  if (lastExportTime.value > 0 && timeSinceLastExport < EXPORT_THROTTLE_INTERVAL) {
    const remainingSeconds = Math.ceil((EXPORT_THROTTLE_INTERVAL - timeSinceLastExport) / 1000)
    ElMessage.warning(`导出操作过于频繁，请等待 ${remainingSeconds} 秒后再试`)
    return
  }
  
  try {
    exportLoading.value = true
    
    const exportParams = {
      status: queryParams.status,
      returnType: queryParams.returnType,
      keyword: queryParams.keyword,
      startDate: queryParams.startDate,
      endDate: queryParams.endDate
    }
    
    const blob = await exportReturnOrderList(exportParams)
    
    if (!blob || blob.size === 0) {
      throw new Error('导出失败：返回数据格式错误')
    }
    
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `返货单列表_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}_${Date.now()}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    
    ElMessage.success('导出成功')
    // 导出成功后才记录时间戳，失败时不限流
    lastExportTime.value = Date.now()
  } catch (error) {
    console.error('导出失败:', error)
    const errorMsg = error.message || '导出失败'
    // 如果是限流错误（数据量过大），使用warning提示
    if (errorMsg.includes('数据量过大') || errorMsg.includes('超过最大限制')) {
      ElMessage.warning(errorMsg)
    } else {
      ElMessage.error(errorMsg)
    }
    // 导出失败时不更新时间戳，允许用户立即重试
  } finally {
    exportLoading.value = false
  }
}

// 初始化状态参数
const initStatusFromQuery = () => {
  if (route.query.status) {
    // 兼容大小写，统一转换为小写
    const status = String(route.query.status).toLowerCase()
    if (queryParams.status !== status) {
      queryParams.status = status
      return true // 返回true表示状态发生了变化
    }
  }
  return false
}

// 执行搜索的函数
const executeSearch = () => {
  const statusChanged = initStatusFromQuery()
  // 如果状态发生变化或者是首次加载，执行搜索
  if (statusChanged || !returnOrderList.value.length) {
    queryParams.page = 1
    loadReturnOrderList()
  }
}

onMounted(() => {
  executeSearch()
})

// 如果使用了 keep-alive，当页面激活时也需要检查状态
onActivated(() => {
  executeSearch()
})

// 监听路由query变化，当从表单页面跳转过来时自动筛选
watch(
  () => route.query.status,
  (newStatus, oldStatus) => {
    // 只有当状态真正发生变化时才执行搜索
    if (newStatus !== oldStatus) {
      if (newStatus) {
        const status = String(newStatus).toLowerCase()
        queryParams.status = status
      } else {
        queryParams.status = ''
      }
      // 状态变化时，重置页码并执行搜索
      queryParams.page = 1
      loadReturnOrderList()
    }
  },
  { immediate: false }
)

// 监听完整路由对象，确保能捕获到query变化（兼容某些路由场景）
watch(
  () => route.fullPath,
  (newPath, oldPath) => {
    // 如果路径变化且包含status参数，执行搜索
    if (newPath !== oldPath && route.query.status) {
      const status = String(route.query.status).toLowerCase()
      if (queryParams.status !== status) {
        queryParams.status = status
        queryParams.page = 1
        loadReturnOrderList()
      }
    }
  },
  { immediate: false }
)
</script>

<style scoped>
.return-order-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
  /* 再向左偏移一些，使筛选条件与表格左侧位置更贴近（参考合同管理） */
  margin-left: -30px;
}

.action-buttons-inline {
  display: inline-flex;
  gap: 8px;
  white-space: nowrap;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 详情弹窗底部按钮样式（对齐合同/报销申请样式） */
.dialog-footer {
  display: flex;
  justify-content: center;
  align-items: center;
}

.footer-center {
  display: flex;
  justify-content: center;
  width: 100%;
}

.footer-buttons {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  width: 100%;
}

.footer-right {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

/* 提交审批按钮淡蓝色样式 */
.submit-approval-btn {
  background-color: #5B9BD5;
  border-color: #5B9BD5;
  color: #fff;
}

.submit-approval-btn:hover {
  background-color: #4A8BC2;
  border-color: #4A8BC2;
}

.submit-approval-btn:disabled {
  background-color: #C0C4CC;
  border-color: #C0C4CC;
  color: #fff;
}
</style>

